import { useEffect, useState } from 'react';
import { Polyline, useMapEvents } from 'react-leaflet';
import { LeafletMouseEvent } from 'leaflet';

import {
  useAppSelector,
  useAppDispatch,
  editModeSelector,
  drawingFromNodeIdSelector,
  currentLayerNodesSelector,
  spacePositionsSelector,
  setDrawingFromNodeId,
} from '../../../store';

/**
 * Renders the preview line when drawing a path.
 * Shows a dashed line from the start marker to the cursor position.
 */
export function DrawPath() {
  const dispatch = useAppDispatch();
  const editMode = useAppSelector(editModeSelector);
  const drawingFromNodeId = useAppSelector(drawingFromNodeIdSelector);
  const nodes = useAppSelector(currentLayerNodesSelector);
  const spacePositions = useAppSelector(spacePositionsSelector);

  const [cursorPosition, setCursorPosition] = useState<[number, number] | null>(null);

  // Get the starting position for the preview line
  // drawingFromNodeId can be:
  // - "space:{spaceId}" for starting from a marker
  // - A node ID (UUID) for starting from a waypoint
  let startPosition: [number, number] | null = null;

  if (drawingFromNodeId) {
    if (drawingFromNodeId.startsWith('space:')) {
      // Starting from a space/marker - use space position
      const spaceId = drawingFromNodeId.replace('space:', '');
      const spaceIdStr = String(spaceId);
      if (spacePositions[spaceIdStr]) {
        startPosition = [spacePositions[spaceIdStr].x, spacePositions[spaceIdStr].y];
      } else {
        // Fallback to node position
        const spaceNode = nodes.find((n) => n.spaceId && String(n.spaceId) === spaceIdStr);
        if (spaceNode) {
          startPosition = [spaceNode.position.x, spaceNode.position.y];
        }
      }
    } else {
      // Starting from a waypoint node (UUID)
      const waypointNode = nodes.find((n) => n.id === drawingFromNodeId);
      if (waypointNode) {
        startPosition = [waypointNode.position.x, waypointNode.position.y];
      }
    }
  }

  // Track mouse movement for preview line and handle right-click to cancel
  useMapEvents({
    mousemove: (e: LeafletMouseEvent) => {
      if (editMode === 'drawingPath' && drawingFromNodeId) {
        setCursorPosition([e.latlng.lat, e.latlng.lng]);
      }
    },
    contextmenu: (e: LeafletMouseEvent) => {
      if (editMode === 'drawingPath' && drawingFromNodeId) {
        e.originalEvent.preventDefault();
        dispatch(setDrawingFromNodeId(null));
      }
    },
  });

  // Clear cursor position when not drawing
  useEffect(() => {
    if (editMode !== 'drawingPath' || !drawingFromNodeId) {
      setCursorPosition(null);
    }
  }, [editMode, drawingFromNodeId]);

  // Don't render anything if not in drawing mode or no start position
  if (editMode !== 'drawingPath' || !startPosition || !cursorPosition) {
    return null;
  }

  return (
    <Polyline
      positions={[startPosition, cursorPosition]}
      pathOptions={{
        color: '#2196F3',
        weight: 3,
        opacity: 0.6,
        dashArray: '8, 8',
        lineCap: 'round',
        interactive: false,
      }}
    />
  );
}
